import { Injectable, OnDestroy } from '@angular/core';
import { Observable, of, Subject, Subscription, timer } from 'rxjs';
import { ToastService } from '../../shared/components/toast/services/toast.service';
import { ChatApi } from '../apis/chat.api';
import { EnumRunControlState } from '../enums/run-control-state.enum';
import { EnumRunPhase } from '../enums/run-phase.enum';
import { IActiveExecution } from '../interfaces/active-execution.interface';
import { IActivity } from '../interfaces/activity.interface';
import { IChatInputMessage } from '../interfaces/chat-input.interface';
import { IChatMessage } from '../interfaces/chat-message.interface';
import { IHITLDecision, IHITLRequest } from '../interfaces/hitl.interface';
import { ChatHistoryStateService } from './chat-history-state.service';
import { ChatSessionsStore } from './chat-sessions.store';
import { ConversationExecution } from './conversation-execution';
import { KnowledgeCacheService } from './knowledge-cache.service';
import { MarkdownFormatterService } from './markdown-formatter.service';

export const SESSION_RETENTION_MS = 30 * 60 * 1000;
export const MAX_RETAINED_SESSIONS = 20;

interface IConversationEntry {
  execution: ConversationExecution;
  lastUsed: number;
}

/** Comandos identificam a conversa; cada tela mantém sua própria seleção. */
@Injectable({ providedIn: 'root' })
export class AyaService implements OnDestroy {
  private removed = new Subject<string | null>();
  readonly removedSession$ = this.removed.asObservable();
  private executions = new Map<string, IConversationEntry>();
  private visible = new Map<string, string>();
  private retention: Subscription;


  constructor(
    private markdownFormatter: MarkdownFormatterService,
    private chatApi: ChatApi,
    private sessions: ChatSessionsStore,
    private historyState: ChatHistoryStateService,
    private toastService: ToastService,
    private knowledgeCache: KnowledgeCacheService
  ) {
    this.retention = timer(60_000, 60_000).subscribe(() => this.prune());
  }

  openConversation(
    sessionId: string,
    snapshot?: {
      messages: IChatMessage[];
      title: string | null;
      execution: IActiveExecution | null;
    }
  ): Observable<void> {
    return this.execution(sessionId).open(snapshot);
  }

  sendMessage(sessionId: string, message: IChatInputMessage): void {
    const execution = this.execution(sessionId);
    if (execution.busy || execution.state.interrupt.value) {
      return;
    }
    const clientMessageId = execution.sendUserMessage(message);
    this.stream(sessionId, execution, message, clientMessageId);
  }

  stopRun(sessionId: string): void {
    this.executions.get(sessionId)?.execution.stopRun();
  }

  /** A conversa retida já tem as mensagens em memória: voltar a ela não passa
   * por carregamento e não deve mostrar esqueleto. */
  hasLoaded(sessionId: string): boolean {
    return !!this.executions.get(sessionId)?.execution.state.loaded;
  }

  /** A conversa nasce sem histórico: buscá-lo seria uma ida à rede para
   * receber lista vazia, entre o envio e a mensagem aparecer na tela. */
  markLoaded(sessionId: string): void {
    this.execution(sessionId).state.loaded = true;
  }

  respondToInterrupt(sessionId: string, decisions: IHITLDecision[]): void {
    this.executions.get(sessionId)?.execution.respondToInterrupt(decisions);
  }

  getMessages$(id: string | null): Observable<IChatMessage[]> {
    return id ? this.execution(id).getMessages$() : of([]);
  }

  getActivities$(id: string | null): Observable<IActivity[]> {
    return id ? this.execution(id).getActivities$() : of([]);
  }

  getPhase$(id: string | null): Observable<EnumRunPhase | null> {
    return id ? this.execution(id).getPhase$() : of(null);
  }

  getRunControl$(id: string | null): Observable<EnumRunControlState> {
    return id
      ? this.execution(id).getRunControl$()
      : of(EnumRunControlState.IDLE);
  }

  getInterrupt$(id: string | null): Observable<IHITLRequest | null> {
    return id ? this.execution(id).getInterrupt$() : of(null);
  }

  getIsTyping$(id: string | null): Observable<boolean> {
    return id ? this.execution(id).getIsTyping$() : of(false);
  }

  getTitle$(id: string | null): Observable<string | null> {
    return id ? this.execution(id).getTitle$() : of(null);
  }

  setVisible(consumer: string, sessionId: string | null): void {
    const previous = this.visible.get(consumer);
    if (previous) {
      const entry = this.executions.get(previous);
      if (entry) {
        entry.lastUsed = Date.now();
      }
    }
    if (sessionId) {
      this.visible.set(consumer, sessionId);
      this.execution(sessionId);
    } else {
      this.visible.delete(consumer);
    }
    this.historyState.setVisible(consumer, sessionId);
    this.prune();
  }

  removeConversation(sessionId: string): void {
    this.executions.get(sessionId)?.execution.dispose();
    this.executions.delete(sessionId);
    this.sessions.removeSession(sessionId);
    this.visible.forEach((id, consumer) => {
      if (id === sessionId) {
        this.visible.delete(consumer);
        this.historyState.setVisible(consumer, null);
      }
    });
    this.removed.next(sessionId);
  }

  /** A limpeza da conta invalida os callbacks antes de apagar o estado observável. */
  clear(): void {
    this.executions.forEach((entry) => entry.execution.dispose());
    this.executions.clear();
    this.visible.forEach((_, consumer) =>
      this.historyState.setVisible(consumer, null)
    );
    this.visible.clear();
    this.sessions.clear();
    this.removed.next(null);
  }

  prune(now = Date.now()): void {
    const visible = new Set(this.visible.values());
    const candidates = [...this.executions.entries()]
      .filter(([id, entry]) => !visible.has(id) && !entry.execution.busy)
      .sort(([, left], [, right]) => left.lastUsed - right.lastUsed);
    for (const [id, entry] of candidates) {
      if (
        this.executions.size <= MAX_RETAINED_SESSIONS &&
        now - entry.lastUsed < SESSION_RETENTION_MS
      ) {
        break;
      }
      this.removeConversation(id);
    }
  }

  showAgentError(message: string): void {
    this.toastService.error(message);
  }

  copyToClipboard(message: IChatMessage): void {
    if (!navigator.clipboard) {
      return;
    }
    const element = document.createElement('div');
    element.innerHTML = message.htmlContent || '';
    navigator.clipboard.writeText(element.textContent || message.content);
  }

  ngOnDestroy(): void {
    this.retention.unsubscribe();
    this.clear();
  }

  private stream(
    sessionId: string,
    execution: ConversationExecution,
    message: IChatInputMessage,
    clientMessageId: string
  ): void {
    execution.streamMessage(sessionId, {
      message: message.text,
      clientMessageId,
      knowledgeDocumentId: message.selectedKnowledgeDocument?.id,
      quickActionId: message.quickActionId,
      attachmentIds: message.attachments?.map((a) => a.attachmentId),
      informationIds: message.selectedInformation ? [message.selectedInformation.id] : undefined
    });
  }

  private execution(sessionId: string): ConversationExecution {
    let entry = this.executions.get(sessionId);
    if (!entry) {
      entry = {
        execution: new ConversationExecution(
          this.markdownFormatter,
          this.chatApi,
          sessionId,
          this.historyState,
          this.toastService,
          this.knowledgeCache,
          this.sessions.ensureSession(sessionId)
        ),
        lastUsed: Date.now()
      };
      this.executions.set(sessionId, entry);
    }
    entry.lastUsed = Date.now();
    return entry.execution;
  }
}
